'use client';

import { useEffect, useRef } from 'react';
import type { ChangeEvent, ClipboardEvent, FocusEvent, KeyboardEvent } from 'react';
import { Input, Label, VisuallyHidden } from '@zillow/constellation-10';
import { codeGrid, codeInputBox } from './codeInput.styles';

export const DEFAULT_CODE_LENGTH = 6;

export type CodeInputProps = {
  /** Accessible label for each box, e.g. `(position, total) => \`Digit ${position} of ${total}\``. */
  digitLabel: (position: number, total: number) => string;
  disabled?: boolean;
  error?: boolean;
  /** Namespaces the generated input ids; pass a `useId()` value when more than one may render. */
  idPrefix: string;
  /** How many characters the code has. */
  length?: number;
  onChange: (code: string) => void;
  value: string;
};

const digitsOnly = (raw: string) => raw.replace(/\D/g, '');

/**
 * A row of single-character boxes backed by one string.
 *
 * The boxes are a presentation detail — callers hold the code as a plain string, so a
 * paste, an autofill, and typing each character all converge on the same value. The code
 * stays gap-free: focus lands on the first empty box, so a character can only ever be
 * appended or replaced, never stranded past a hole.
 */
export function CodeInput({
  digitLabel,
  disabled,
  error,
  idPrefix,
  length = DEFAULT_CODE_LENGTH,
  onChange,
  value,
}: CodeInputProps) {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  // Focus moves in the same tick as onChange, before the new value has been rendered
  // back down. The guard below has to read what the code is *about* to be, not the prop
  // it still sees, or focus bounces back to the box that just took a character.
  const latestValue = useRef(value);
  latestValue.current = value;

  const commit = (next: string) => {
    latestValue.current = next;
    onChange(next);
  };

  const focusBox = (index: number) => {
    inputRefs.current[Math.max(0, Math.min(index, length - 1))]?.focus();
  };

  // A cleared code means the caller is asking for a fresh one — a rejected code, a resend.
  // Without this the caret stays in whatever box it was in, so typing resumes in the middle
  // of an empty row, against this component's gap-free contract.
  const previousValue = useRef(value);
  useEffect(() => {
    const wasCleared = previousValue.current.length > 0 && value.length === 0;
    // Leave the ref alone while disabled: advancing it here would consume the clear, and the
    // refocus could never be recovered once entry reopens.
    if (wasCleared && disabled) return;

    previousValue.current = value;
    if (wasCleared) {
      focusBox(0);
    }
  }, [value, disabled]);

  const replaceAt = (index: number, digit: string) => {
    const chars = value.split('');
    if (digit) {
      chars[index] = digit;
    } else {
      chars.splice(index, 1);
    }
    return chars.join('').slice(0, length);
  };

  const handleFocus = (index: number) => (event: FocusEvent<HTMLInputElement>) => {
    if (index > latestValue.current.length) {
      focusBox(latestValue.current.length);
      return;
    }
    // Selecting lets the next keystroke overwrite the character instead of being
    // swallowed by maxLength.
    event.target.select();
  };

  // Spreads whatever lands in a box across the rest. Autofill delivers the whole code
  // into a single box by setting its value programmatically — that bypasses maxLength and
  // fires `change`, not `paste`, so it arrives here rather than in handlePaste.
  const fillFrom = (index: number, digits: string) => {
    const next = (value.slice(0, index) + digits).slice(0, length);
    commit(next);
    focusBox(next.length);
  };

  const handleChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const digits = digitsOnly(event.target.value);
    if (digits.length > 1) {
      fillFrom(index, digits);
      return;
    }
    commit(replaceAt(index, digits));
    if (digits) {
      focusBox(index + 1);
    }
  };

  const handleKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !value[index] && index > 0) {
      event.preventDefault();
      commit(replaceAt(index - 1, ''));
      focusBox(index - 1);
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusBox(index - 1);
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusBox(index + 1);
    }
  };

  // Handled here rather than through onChange so a pasted code lands across every box
  // instead of being truncated to the one that happened to have focus.
  const handlePaste = (index: number) => (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = digitsOnly(event.clipboardData.getData('text'));
    if (!pasted) {
      return;
    }
    event.preventDefault();
    fillFrom(index, pasted);
  };

  return (
    <div className={codeGrid}>
      {Array.from({ length }, (_, index) => {
        const id = `${idPrefix}-digit-${index}`;

        return (
          <div key={id}>
            <VisuallyHidden asChild>
              <Label htmlFor={id}>{digitLabel(index + 1, length)}</Label>
            </VisuallyHidden>
            <Input
              id={id}
              ref={(node) => {
                inputRefs.current[index] = node;
              }}
              className={codeInputBox}
              type="text"
              inputMode="numeric"
              // Browsers offer the code to the first box only; fillFrom spreads whatever
              // arrives across the rest, whether it comes via change or paste.
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              maxLength={1}
              size="lg"
              value={value[index] ?? ''}
              error={error}
              disabled={disabled}
              aria-invalid={error || undefined}
              // eslint-disable-next-line jsx-a11y/no-autofocus -- the control exists to collect this code; landing focus in box 1 is the accessible behavior here
              autoFocus={index === 0}
              onFocus={handleFocus(index)}
              onChange={handleChange(index)}
              onKeyDown={handleKeyDown(index)}
              onPaste={handlePaste(index)}
            />
          </div>
        );
      })}
    </div>
  );
}
